import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CloudSolutions = () => {
  // Hero Banner Section
  const HeroBannerSection = () => (
    <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920" 
          alt="Cloud Solutions" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Scalable & Secure <span className="text-green-500">Cloud Solutions</span>
        </h1>
        <p className="text-white text-xl max-w-3xl mx-auto">
          Transform your business with powerful cloud infrastructure. From migration to management, we help you leverage the cloud for growth, flexibility, and cost savings.
        </p>
      </div>
    </section>
  );

  // Overview Section
  const OverviewSection = () => (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-6">
              Embrace the Power of <span className="text-green-600">Cloud Computing</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              At Plexgate, we provide comprehensive cloud solutions that help businesses modernize their infrastructure, reduce costs, and scale effortlessly. Whether you're looking to migrate to the cloud, optimize existing cloud resources, or build cloud-native applications, we have the expertise to guide you.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Our cloud specialists work with leading platforms including AWS, Microsoft Azure, and Google Cloud to design and implement solutions tailored to your specific business needs. We ensure your cloud infrastructure is secure, scalable, and cost-effective.
            </p>
          </div>
          <div>
            <img 
              src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&h=600&fit=crop" 
              alt="Cloud Solutions" 
              className="rounded-lg shadow-2xl w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );

  // Services Section
  const ServicesSection = () => {
    const services = [
      {
        title: 'Cloud Migration',
        description: 'Seamless migration of your applications, data, and infrastructure to the cloud with minimal downtime and risk.',
        icon: '☁️'
      },
      {
        title: 'Cloud Infrastructure',
        description: 'Design and deployment of scalable, reliable cloud infrastructure using IaaS, PaaS, and serverless technologies.',
        icon: '🏗️'
      },
      {
        title: 'Cloud Storage Solutions',
        description: 'Secure and scalable cloud storage with automated backups, disaster recovery, and data archiving.',
        icon: '💾'
      },
      {
        title: 'Cloud Security',
        description: 'Comprehensive security measures including encryption, access controls, compliance management, and threat monitoring.',
        icon: '🔒'
      },
      {
        title: 'Multi-Cloud Solutions',
        description: 'Strategic use of multiple cloud providers to optimize performance, reduce vendor lock-in, and improve resilience.',
        icon: '🌐'
      },
      {
        title: 'Cloud Optimization',
        description: 'Cost optimization, performance tuning, resource management, and continuous monitoring of your cloud environment.',
        icon: '📊'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-green-600">Cloud Services</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              End-to-end cloud solutions for modern businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Cloud Platforms Section
  const PlatformsSection = () => {
    const platforms = [
      {
        name: 'Amazon Web Services (AWS)',
        services: ['EC2', 'S3', 'Lambda', 'RDS', 'CloudFront'],
        icon: '🟠'
      },
      {
        name: 'Microsoft Azure',
        services: ['Virtual Machines', 'Azure Storage', 'Azure Functions', 'SQL Database', 'App Service'],
        icon: '🔵'
      },
      {
        name: 'Google Cloud Platform (GCP)',
        services: ['Compute Engine', 'Cloud Storage', 'Cloud Functions', 'Cloud SQL', 'Kubernetes Engine'],
        icon: '🔴'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Cloud Platforms We <span className="text-green-600">Support</span>
            </h2>
            <p className="text-gray-600">Certified experts in leading cloud platforms</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 border-l-4 border-blue-500">
                <div className="text-5xl mb-4">{platform.icon}</div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{platform.name}</h3>
                <ul className="space-y-2">
                  {platform.services.map((service, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      <span className="text-gray-600">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Benefits Section
  const BenefitsSection = () => {
    const benefits = [
      {
        title: 'Cost Savings',
        description: 'Reduce capital expenses with pay-as-you-go pricing. No upfront hardware costs or maintenance fees.',
        icon: '💰'
      },
      {
        title: 'Scalability',
        description: 'Scale resources up or down instantly based on demand. Handle traffic spikes without infrastructure planning.',
        icon: '📈'
      },
      {
        title: 'Reliability',
        description: '99.99% uptime SLA, automated backups, and disaster recovery ensure business continuity.',
        icon: '✅'
      },
      {
        title: 'Global Reach',
        description: 'Deploy applications across multiple regions worldwide for low latency and better user experience.',
        icon: '🌍'
      },
      {
        title: 'Security',
        description: 'Enterprise-grade security with encryption, compliance certifications, and advanced threat protection.',
        icon: '🛡️'
      },
      {
        title: 'Innovation',
        description: 'Access cutting-edge technologies like AI/ML, IoT, serverless computing, and big data analytics.',
        icon: '🚀'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Benefits of <span className="text-green-600">Cloud Computing</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="text-4xl">{benefit.icon}</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Migration Process Section
  const ProcessSection = () => {
    const steps = [
      {
        number: '01',
        title: 'Assessment',
        description: 'Analyze your current infrastructure, applications, and business requirements to develop a migration strategy.'
      },
      {
        number: '02',
        title: 'Planning',
        description: 'Design cloud architecture, select appropriate services, estimate costs, and create detailed migration roadmap.'
      },
      {
        number: '03',
        title: 'Migration',
        description: 'Execute the migration in phases, starting with non-critical systems, testing thoroughly at each stage.'
      },
      {
        number: '04',
        title: 'Optimization',
        description: 'Fine-tune performance, optimize costs, implement automation, and provide ongoing management and support.'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our Cloud Migration <span className="text-green-600">Process</span>
            </h2>
            <p className="text-gray-600">A proven methodology for successful cloud adoption</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gray-50 rounded-lg p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="bg-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };  // CTA Section
  const CTASection = () => (
    <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920" 
          alt="Team" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to Move to the <span className="text-green-500">Cloud</span>?
        </h2>
        <p className="text-white text-xl mb-8 max-w-3xl mx-auto">
          Get a free cloud assessment and discover how cloud computing can transform your business.
        </p>
        <div className="flex gap-4 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
          >
            Get Free Assessment →
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-3 rounded-full hover:bg-gray-100 transition-colors font-medium"
          >
            View All Services
          </Link>
        </div>
      </div>
    </section>
  );

  return (
    <div className="cloud-solutions-page">
      <HeroBannerSection />
      <OverviewSection />
      <ServicesSection />
      <PlatformsSection />
      <BenefitsSection />
      <ProcessSection />
      <FAQSection />
      <CTASection />
    </div>
  );
};



  // FAQ Section
  const FAQSection = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);
    const faqs = [
      {
        question: 'What is cloud computing?',
        answer: 'Cloud computing delivers computing services (servers, storage, databases, networking, software) over the internet, allowing you to pay only for what you use without managing physical infrastructure.'
      },
      {
        question: 'How long does cloud migration take?',
        answer: 'Timeline varies based on complexity and data volume. A simple migration might take 2-4 weeks, while enterprise migrations can take 3-12 months. We provide detailed timelines during the assessment phase.'
      },
      {
        question: 'Is cloud computing secure?',
        answer: 'Yes! Cloud providers invest heavily in security with advanced encryption, access controls, and compliance certifications (HIPAA, SOC 2, ISO 27001). We implement additional security layers specific to your needs.'
      },
      {
        question: 'Will moving to the cloud save money?',
        answer: 'Most businesses save 20-50% on IT costs by eliminating hardware purchases, maintenance, and reducing energy costs. We provide cost analysis during consultation to estimate your savings.'
      },
      {
        question: 'Can we use multiple cloud providers?',
        answer: 'Absolutely! Multi-cloud strategies leverage the best services from each provider, reduce vendor lock-in, and improve resilience. We help design and manage multi-cloud environments.'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Frequently Asked <span className="text-green-600">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-lg">{faq.question}</span>
                  <span className="text-2xl text-gray-600">
                    {expandedFAQ === index ? '−' : '+'}
                  </span>
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };


export default CloudSolutions;